import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useAsyncFn } from 'react-use';
import toLower from 'lodash/toLower';
import { Input, Button, message, Typography, Divider } from 'antd';
import { useHistory, useLocation, useParams, Redirect } from 'react-router-dom';

import { passwordValidator } from 'utils';
import { NAME_REGEX, SINGLE_REGEX, MAP_BRANCH } from 'constants/index';
import Form from 'components/Form';
import Modal from 'components/Modal';
import commonService from 'services/common.service';
import userService from 'services/users.service';

import styles from './SignUp.module.less';
import useBreakpoints from 'hooks/useBreakpoints';
import classNames from 'classnames';
import PageMeta from 'components/PageMeta/PageMeta';
import { useFlags } from 'launchdarkly-react-client-sdk';

export default function SignUp() {
  const { showFamilySignupLinks } = useFlags();

  const [form] = Form.useForm();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const { lg } = useBreakpoints();
  const { pathname } = useLocation();
  const isFamily = pathname.includes('family');
  const history = useHistory();
  const { branchId } = useParams();

  const handleSubmit = useCallback(
    async (values) => {
      try {
        setLoading(true);
        setEmail(values.email);
        const data = {
          email: toLower(values.email),
          password: values.password,
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.phone,
          businessName: values.businessName,
          preferredProgramSponsorId: branchId,
        };
        await commonService.post(isFamily ? '/users/families/signup' : `/users/providers/signup`, data);
        setVisible(true);
      } catch (error) {
        newrelic.noticeError(error);
        // error coming from api contains other fields and ids so just showing the right message here.
        if (error.apierror?.message?.includes?.('An account with the given email already exists')) {
          message.error('An account with the given email already exists.', 5);
        } else {
          message.error(error.apierror?.message || error.message || 'Unable to create user account.', 5);
        }
      } finally {
        setLoading(false);
      }
    },
    [branchId, isFamily],
  );

  const [{ loading: sendingEmail }, resendEmail] = useAsyncFn(async () => {
    try {
      await userService.resendConfirmationEmail({ username: toLower(email) });
      message.success(<span data-testid="re-confirmation-message">Confirmation email has been sent again.</span>, 5);
    } catch (error) {
      message.error(
        <span data-testid="re-confirmation-error">
          {error?.apierror?.message || error?.message || 'Unable to resend confirmation email.'}
        </span>,
        5,
      );
      newrelic.noticeError(error);
    }
  }, [email]);

  // Modal asks if the user has applied for military fee assistance before
  const [hideModal, setHideModal] = useState(false);
  const firstNameInput = React.useRef();

  if (isFamily && MAP_BRANCH[branchId] === undefined) {
    return <Redirect to="/family/signup" />;
  }

  if (isFamily && !showFamilySignupLinks) {
    return <Redirect to="/" />;
  }

  return (
    <div id="signup-form-container">
      <PageMeta title={`${isFamily ? 'Family' : 'Provider'} Sign Up`} />

      {/* Modal asks if the user has applied for military fee assistance before */}
      {!isFamily && (
        <SignupScreeningModal hideModal={hideModal} setHideModal={setHideModal} firstNameInput={firstNameInput} />
      )}

      <h1 className="title">{isFamily ? 'Family' : 'Provider'} Signup</h1>

      <Form
        onFinish={handleSubmit}
        form={form}
        layout="vertical"
        validateTrigger="onBlur"
        className={classNames('mt-4', styles.signUp)}
      >
        <Form.Item
          name="firstName"
          label="first name"
          rules={[
            {
              required: true,
              message: 'First Name is required',
            },
            () => ({
              validator(rule, value) {
                if (typeof value === 'string' && value && !value.trim()) return Promise.reject('Invalid First Name');
                if (!value || SINGLE_REGEX.test(value) || NAME_REGEX.test(value)) {
                  return Promise.resolve();
                }
                return Promise.reject('Invalid First Name');
              },
            }),
          ]}
        >
          <Input placeholder="First Name" autoComplete="off" ref={firstNameInput} />
        </Form.Item>

        <Form.Item
          name="lastName"
          label="last name"
          rules={[
            { required: true, message: 'Last Name is required' },
            () => ({
              validator(rule, value) {
                if (typeof value === 'string' && value && !value.trim()) return Promise.reject('Invalid Last Name');
                if (!value || SINGLE_REGEX.test(value) || NAME_REGEX.test(value)) {
                  return Promise.resolve();
                }
                return Promise.reject('Invalid Last Name');
              },
            }),
          ]}
        >
          <Input placeholder="Last Name" autoComplete="off" />
        </Form.Item>

        {!isFamily && (
          <Form.Item
            name="businessName"
            label="Business/Organization Name"
            rules={[{ required: true, message: 'Business/Organization Name is required' }]}
          >
            <Input autoComplete="off" />
          </Form.Item>
        )}

        <Form.Item
          name="email"
          label="email address"
          rules={[
            { required: true, message: 'Email is required' },
            { type: 'email', message: 'Email is invalid' },
          ]}
        >
          <Input placeholder="Email Address (abc@gmail.com)" />
        </Form.Item>

        <Form.Item
          name="password"
          label="password"
          // hasFeedback
          rules={[{ required: true, message: 'Password is required' }, passwordValidator]}
        >
          <PasswordFormItem />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="re-enter password"
          // hasFeedback
          rules={[
            { required: true, message: 'Password is required' },
            ({ getFieldValue }) => ({
              validator(rule, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject('Password does not match');
              },
            }),
          ]}
          dependencies={['password']}
        >
          <Input type="password" placeholder="Repeat Password" />
        </Form.Item>

        <Button type="primary" className="action" htmlType="submit" loading={loading} disabled={loading}>
          Sign up
        </Button>
      </Form>

      <Modal
        maskClosable={false}
        width={lg ? 770 : 'auto'}
        visible={visible}
        setVisible={(v) => {
          setVisible(false);
          /* istanbul ignore else */
          if (!v) {
            history.push('/signin');
          }
        }}
        getContainer="#signup-form-container"
      >
        <span className="section-title">Please Verify your email address</span>
        <h3 className="heading mt-5">Thank you for registering with Child Care Aware of America!</h3>
        <p className="text">A verification link has been sent to your email account.</p>
        <p className="text">
          Please click on the link that has been sent to your email <b>{email}</b> and continue the application process.
        </p>
        <p className="text">Please check your spam or junk mailbox and mark it as safe.</p>
        <p className="text">Please read all documentation provided carefully before starting the process.</p>
        <div className="flex flex-col sm:flex-row sm:justify-between space-y-2 sm:space-y-0 mt-10">
          <Button onClick={resendEmail} loading={sendingEmail} disabled={sendingEmail}>
            Click here if you did not receive an email
          </Button>
          <Button
            loading={sendingEmail}
            disabled={sendingEmail}
            className="w-40"
            onClick={() => history.push('/signin')}
          >
            OK
          </Button>
        </div>
      </Modal>
    </div>
  );
}

SignUp.propTypes = {
  className: PropTypes.string,
};
SignUp.defaultProps = {
  className: '',
};

const PasswordFormItem = (formItemProps) => {
  return (
    <div>
      <p className="mb-2 opacity-65 leading-5">
        Password must have 8 characters, including 1 upper case letter, 1 numeric character, and 1 special character.
      </p>

      <Input type="password" placeholder="Password" {...formItemProps} />
    </div>
  );
};

const SignupScreeningModal = ({ hideModal, setHideModal, firstNameInput }) => (
  <Modal
    closable={false}
    open={!hideModal}
    title={
      <Typography.Title level={3}>
        Are you already a participating provider for the Military Fee Assistance programs at Child Care Aware® of
        America?
      </Typography.Title>
    }
    width={750}
    footer={
      <div>
        <Divider />

        <div className="flex gap-4 justify-end">
          <a href="mailto:providerservices@usa.childcareaware.org" className="no-underline">
            <Button>Contact Provider Services</Button>
          </a>

          <Button
            type="primary"
            onClick={() => {
              setHideModal(true);
              firstNameInput?.current?.focus?.();
            }}
            data-testid="signup-close-modal"
          >
            Create a new account
          </Button>
        </div>
      </div>
    }
  >
    If so, you may already have an account.{' '}
    <a href="mailto:providerservices@usa.childcareaware.org" className="underline hover:underline">
      Contact Provider Services
    </a>{' '}
    if you'd like to do any of the following:
    <ul className="list-disc ml-5 mt-2">
      <li>Update or make changes to your existing provider file</li>
      <li>Change your business information</li>
      <li>Cancel your participation in a fee assistance program</li>
    </ul>
  </Modal>
);
